// Customer Story — customer facts (industry, location, website).
// Reference metadata about the featured company. Rendered twice: stacked in
// the sticky desktop sidebar beside the article, and as an inline card on
// mobile where that sidebar is hidden. Every field is optional in the case
// study frontmatter, so the block hides itself when none are present.

import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

export interface CustomerStoryFactLabels {
  industry: string;
  location: string;
  website: string;
}

interface CustomerStoryFactsProps {
  labels: CustomerStoryFactLabels;
  industry?: string;
  location?: string;
  websiteUrl?: string;
  /** "stack" for the 196px sidebar, "row" for the full-width mobile card. */
  layout?: "stack" | "row";
  className?: string;
}

/** `https://www.myomaster.co.uk/` → `myomaster.co.uk` — the raw URL is noise. */
function displayHost(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function CustomerStoryFacts({
  labels,
  industry,
  location,
  websiteUrl,
  layout = "stack",
  className,
}: CustomerStoryFactsProps) {
  const facts = [
    industry
      ? { key: "industry", label: labels.industry, value: industry }
      : null,
    location
      ? { key: "location", label: labels.location, value: location }
      : null,
    websiteUrl
      ? {
          key: "website",
          label: labels.website,
          value: displayHost(websiteUrl),
          href: websiteUrl,
        }
      : null,
  ].filter((fact) => fact !== null);

  if (facts.length === 0) return null;

  return (
    <dl
      className={cn(
        "flex",
        layout === "stack"
          ? "flex-col gap-4 p-3"
          : "flex-row flex-wrap gap-x-10 gap-y-5",
        className
      )}
    >
      {facts.map((fact) => (
        <div key={fact.key} className="flex min-w-0 flex-col gap-1">
          <dt className="text-grey-500 text-[12px] leading-[1.4] tracking-[-0.24px]">
            {fact.label}
          </dt>
          <dd className="text-grey-950 text-[12px] leading-[1.4] tracking-[-0.24px]">
            {fact.href ? (
              <Link
                href={fact.href}
                external
                className="underline underline-offset-2 hover:opacity-70"
              >
                {fact.value}
              </Link>
            ) : (
              fact.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
